import { useEffect } from 'react';

import { useForm, useWatch } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { useShallow } from 'zustand/react/shallow';

import { CountrySelect } from '@/entities/country';

import {
  Button,
  DashboardFormMessage,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
} from '@/shared/ui';

import {
  SendMoneyBankStepData,
  sendMoneyBankSchema,
} from '../../../../model/schema_new';
import { useSendMoney } from '../../../../model/useSendMoney';

export const BankStepForm = () => {
  const { setStep, updateFormData, data } = useSendMoney(
    useShallow((state) => state),
  );
  const form = useForm<SendMoneyBankStepData>({
    defaultValues: {
      address: {
        country: {},
        street: '',
        postcode: '',
        city: '',
      },
      name: '',
    },
    resolver: zodResolver(sendMoneyBankSchema),
    shouldFocusError: false,
    mode: 'onChange',
  });

  useEffect(() => {
    if (data?.name) {
      form.setValue('name', data?.name);
      form.setValue('address', data?.address);
      form.setValue('address.country', data?.address?.country);
    }
  }, []);

  const formData = useWatch({
    control: form.control,
  });

  //SubmitHandler<SendMoneyBankStepData>
  const onSubmit = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setStep('address');
      updateFormData({
        ...formData,
      });
    }
  };

  return (
    <div className="mt-5 flex-1">
      <Form {...form}>
        <form
          className="flex h-full w-full flex-col justify-between"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="relative flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-1">
              <FormField
                control={form.control}
                name="address.country"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CountrySelect
                        placeholder="Country"
                        value={field.value}
                        onSelect={(data) => {
                          field.onChange({ id: data.id, name: data.name });
                        }}
                        prefixQueryKey="send-bank"
                        isInvalid={!!form.formState.errors.address?.country}
                      />
                    </FormControl>
                    {!!form.formState.errors.address?.country && (
                      <p className="mt-2 px-3 px-4 pb-2 pt-0.5 text-start text-[13px] text-negative-500 typography-12r">
                        Field is required
                      </p>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="bank-name"
                        placeholder="Bank name"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {form.formState.errors.name?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-12 pb-2 flex flex-col gap-y-1">
              <FormField
                control={form.control}
                name="address.street"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="bank-address"
                        placeholder="Bank address"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {form.formState.errors.address?.street?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address.postcode"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="bank-postcode"
                        placeholder="Postcode"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {form.formState.errors.address?.postcode?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address.city"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="bank-city"
                        placeholder="City"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {form.formState.errors.address?.city?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
            </div>
          </div>

        </form>
      </Form>
      <Button
        className="sticky bottom-1 w-full"
        size="lg"
        type="submit"
        variant="brand-500"
        onClick={onSubmit}
      >
        Continue
      </Button>
    </div>
  );
};
